import { Collapse, Grid } from '@mui/material'
import { KDatePicker } from 'components/core/KDatePicker'
import { useCurrentStore, useOrdering } from 'hooks'
import { useTranslation } from 'react-i18next'
import {
  canSetOrderDate,
  getTotalDelayTimeInMinutes,
  calculateAvailableTimeSlots,
  getStartOfDay,
  calculateEarliestAvailableTime,
  calculateCateringEarliestOrderDate,
} from 'services'
import { SubmitOrderSectionProps } from './SubmitOrderSectionProps'
import { useCallback, useEffect, useState } from 'react'
import { KSelect } from 'components/core/KSelect'
import { isEmpty } from 'lodash'
import { timestampFormat } from 'models'
import moment from 'moment'

interface OrderInfoTimeInputState {
  timeSlots: moment.Moment[]
  canSetDate: boolean
  selectTimeLabel: string
  selectDateLabel: string
}

const orderTypeLabels: { [key: string]: string } = {
  delivery: 'Delivery',
  pickup: 'Pickup',
  dinein: 'Dine-in',
}

export const OrderInfoTimeInput = ({ form }: SubmitOrderSectionProps) => {
  const formFieldName = 'orderInfo.time'
  const { t } = useTranslation()
  const {
    register,
    setError,
    clearErrors,
    setValue,
    formState: { errors },
  } = form
  const { store, currentTime, parseTime, formatTime, timezone, isOpening } =
    useCurrentStore()
  const { order, saveOrderProps, cateringPreparationDays } = useOrdering()

  const isASAP = order?.is_asap
  const orderType = order?.order_type

  const [date, setDate] = useState(
    order?.timeIsoFormat ? parseTime(order?.timeIsoFormat) : currentTime(),
  )

  const [state, setState] = useState<OrderInfoTimeInputState>({
    timeSlots: [],
    canSetDate: false,
    selectTimeLabel: '',
    selectDateLabel: '',
  })

  const clearOrderTime = useCallback(() => {
    saveOrderProps({
      time: undefined,
      timeIsoFormat: undefined,
    })
    setValue(formFieldName, undefined)
  }, [saveOrderProps, setValue, formFieldName])

  const setOrderTime = useCallback(
    (timeIsoString: string) => {
      const selectedTime = parseTime(timeIsoString)

      if (
        selectedTime &&
        selectedTime.isValid() &&
        !selectedTime.isSame(date, 'day')
      ) {
        setDate(selectedTime.clone())
      }

      saveOrderProps({
        time: formatTime(timeIsoString, timestampFormat),
        timeIsoFormat: timeIsoString,
      })
      setValue(formFieldName, timeIsoString)
      clearErrors(formFieldName)
    },
    [
      saveOrderProps,
      formatTime,
      setValue,
      clearErrors,
      formFieldName,
      parseTime,
      date,
    ],
  )

  useEffect(() => {
    if (!store || !orderType) return

    if (isASAP === true) {
      // Clear order time selection selecting ASAP
      if (order?.timeIsoFormat) {
        clearOrderTime()
      }
      clearErrors(formFieldName)
      return
    }

    // Select Later business logic
    const orderTypeLabel = orderTypeLabels[orderType]
    const selectTimeLabel = t(`${orderTypeLabel} time`)
    const selectDateLabel = t(`${orderTypeLabel} date`)

    let canSetDate = canSetOrderDate(store, orderType) ?? false

    // Orders containing catering items always require picking a date,
    // regardless of the store level catering setting.
    if (cateringPreparationDays > 0) {
      canSetDate = true

      // The order can only be fulfilled after the minimum preparation time
      // of its catering categories. Push the selected date forward when it
      // violates that minimum, the effect runs again with the new date.
      const earliestOrderDate = calculateCateringEarliestOrderDate(
        cateringPreparationDays,
        timezone,
      )
      if (date.isBefore(earliestOrderDate, 'day')) {
        if (order?.timeIsoFormat) {
          clearOrderTime()
        }
        setDate(earliestOrderDate)
        return
      }
    }

    // Check if store is currently open
    const isStoreOpen = isOpening === true

    // If store is closed, calculate the earliest available time
    let baseTime = currentTime()
    if (!isStoreOpen) {
      const earliestTime = calculateEarliestAvailableTime(store)
      if (earliestTime) {
        // When canSetDate is false (no catering), only use earliest time
        // if it's on the same day (e.g. second business period today).
        // Future-day slots should not be shown without a date picker.
        if (canSetDate || earliestTime.isSame(currentTime(), 'day')) {
          baseTime = earliestTime
          if (
            !baseTime.isSame(date, 'day') &&
            date.isSame(currentTime(), 'day')
          ) {
            setDate(baseTime)
          }
        }
      }
    }

    const delayTime = getTotalDelayTimeInMinutes(store, orderType)

    const day = getStartOfDay(date, timezone)

    // Catering orders always calculate time slots from the selected date
    // since today is never a valid option for them.
    // Note: don't gate this on order?.timeIsoFormat — the date onChange
    // handler clears it right before this effect reruns, which made this
    // flag always false right after picking a future date and reset the
    // selection back to today.
    const useSelectedDateForCalculation =
      (canSetDate && !date.isSame(currentTime(), 'day')) ||
      cateringPreparationDays > 0
    const calculationBaseTime = useSelectedDateForCalculation ? day : baseTime

    const timeSlots =
      calculateAvailableTimeSlots(
        calculationBaseTime,
        delayTime,
        store.biz_hours,
        timezone,
      ) ?? []

    setState({
      timeSlots,
      canSetDate,
      selectTimeLabel,
      selectDateLabel,
    })

    if (timeSlots.length === 0) {
      // No time slots available
      const message = canSetDate
        ? t(
            'No available time for selected date. Change to another date or different type of order.',
          )
        : t('No available time today. Change to another type of order.')
      setError(formFieldName, { message })

      // Clear time if selected before
      if (order?.timeIsoFormat) {
        clearOrderTime()
      }
    } else {
      // Having time slots, need to make sure the selected time is still available.
      // If not then clear the selected time.
      if (order?.timeIsoFormat) {
        const selectedTime = moment(order?.timeIsoFormat)
        if (timeSlots.every((e) => !e.isSame(selectedTime))) {
          setOrderTime(timeSlots[0].format())
        }
      } else {
        setOrderTime(timeSlots[0].format())
      }
      clearErrors(formFieldName)
    }
  }, [orderType, isASAP, date, isOpening, cateringPreparationDays]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Collapse in={isASAP !== true}>
      <Grid
        container
        spacing={2.5}
        sx={{ flexDirection: state.canSetDate ? 'row-reverse' : 'row' }}
      >
        <Grid item xs={12} sm={6}>
          <KSelect
            label={state.selectTimeLabel}
            helperText={errors?.orderInfo?.time?.message}
            error={!!errors?.orderInfo?.time}
            required
            {...register(formFieldName, {
              validate: (value) => {
                if (isASAP !== true && isEmpty(value))
                  return t('{{label}} is required', {
                    label: state.selectTimeLabel,
                  })
              },
              onChange: (e) => {
                const value = e.target.value
                setValue(formFieldName, value)
                if (value) {
                  setOrderTime(value)
                } else {
                  clearOrderTime()
                }
              },
            })}
            options={state.timeSlots?.map((e) => ({
              key: e.clone().format('hh:mm A'),
              value: e.format(),
            }))}
            value={order?.timeIsoFormat}
          />
        </Grid>

        {state.canSetDate && (
          <Grid item xs={12} sm={6}>
            <KDatePicker
              required
              label={state.selectDateLabel}
              value={date}
              timezone={store?.timezone}
              disablePast
              minDate={
                cateringPreparationDays > 0
                  ? calculateCateringEarliestOrderDate(
                      cateringPreparationDays,
                      timezone,
                    )
                  : undefined
              }
              maxDate={currentTime().add(cateringPreparationDays + 30, 'days')}
              helperText={
                cateringPreparationDays > 0
                  ? t(
                      'Your order contains catering items that require at least {{days}} day(s) of preparation.',
                      { days: cateringPreparationDays },
                    )
                  : undefined
              }
              onChange={(value) => {
                if (value && !value.isSame(date, 'day')) {
                  if (order?.timeIsoFormat) {
                    clearOrderTime()
                  }
                  setDate(value)
                }
              }}
            />
          </Grid>
        )}
      </Grid>
    </Collapse>
  )
}
